import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useEffect, useState } from 'react';
import { useDataStore } from '../store/dataStore';
import { useAuthStore } from '../store/authStore';
import { teamRoles } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  UserCog,
  Phone,
  Mail,
  Trash2,
  Loader2,
  Camera,
  Video,
  Plane,
  User,
  Lock,
  Key,
  Edit2,
  ShieldCheck
} from 'lucide-react';

const roleIcons = {
  photographer: Camera,
  videographer: Video,
  drone_operator: Plane,
  assistant: User
};

export default function TeamPage() {
  const {
    team,
    teamLoading,
    fetchTeam,
    addTeamMember,
    deleteTeamMember
  } = useDataStore();

  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: 'photographer',
    phone: '',
    email: '',
    password: '',
    enableLogin: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Lütfen isim girin');
      return;
    }
    if (formData.enableLogin) {
      if (!formData.email) { toast.error('Giriş için e-posta zorunludur'); return; }
      if (!editing && (!formData.password || formData.password.length < 6)) {
        toast.error('Şifre en az 6 karakter olmalı'); return;
      }
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        role: formData.role,
        phone: formData.phone,
        email: formData.email,
      };
      if (formData.enableLogin && formData.password) payload.password = formData.password;

      const API = RUNTIME_BACKEND_URL;
      const token = useAuthStore.getState().token;
      let r;
      if (editing) {
        r = await fetch(`${API}/api/team/${editing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
      } else {
        r = await fetch(`${API}/api/team`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
      }
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'İşlem başarısız');
      }
      await fetchTeam();
      toast.success(editing ? 'Ekip üyesi güncellendi' : 'Ekip üyesi eklendi');
      closeDialog();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setFormData({ name: '', role: 'photographer', phone: '', email: '', password: '', enableLogin: false });
    setIsOpen(true);
  };

  const openEdit = (member) => {
    setEditing(member);
    setFormData({
      name: member.name || '',
      role: member.role || 'photographer',
      phone: member.phone || '',
      email: member.email || '',
      password: '',
      enableLogin: !!member.can_login,
    });
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setEditing(null);
    setFormData({ name: '', role: 'photographer', phone: '', email: '', password: '', enableLogin: false });
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`"${name}" üyesini silmek istediğinize emin misiniz?`)) {
      try {
        await deleteTeamMember(id);
        toast.success('Ekip üyesi silindi');
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Ekip</h1>
          <p className="text-muted-foreground mt-1">Ekip üyelerinizi yönetin (Maks. 5 üye)</p>
        </div>
        <Dialog open={isOpen} onOpenChange={(o) => o ? openNew() : closeDialog()}>
          <DialogTrigger asChild>
            <Button data-testid="add-team-btn" disabled={team.length >= 5} onClick={openNew}>
              <Plus className="h-4 w-4 mr-2" />
              {team.length >= 5 ? 'Limit Doldu' : 'Yeni Üye'}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? 'Ekip Üyesini Düzenle' : 'Yeni Ekip Üyesi Ekle'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Ad Soyad *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ad Soyad"
                  data-testid="team-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Görev</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="photographer">Fotoğrafçı</SelectItem>
                    <SelectItem value="videographer">Kameraman</SelectItem>
                    <SelectItem value="drone_operator">Drone Operatörü</SelectItem>
                    <SelectItem value="assistant">Asistan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="05XX XXX XX XX"
                />
              </div>
              <div className="space-y-2">
                <Label>E-posta {formData.enableLogin && <span className="text-red-500">*</span>}</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="ornek@email.com"
                />
              </div>

              {/* Giriş Yetkisi Bölümü */}
              <div className="border-t pt-4 space-y-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.enableLogin}
                    onChange={(e) => setFormData({ ...formData, enableLogin: e.target.checked })}
                    className="mt-1"
                    data-testid="team-enable-login"
                  />
                  <div>
                    <div className="text-sm font-medium flex items-center gap-1">
                      <ShieldCheck className="h-4 w-4 text-emerald-600" />
                      Sisteme giriş yapabilsin
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Aktifleşirse ekip üyesi kendi e-posta ve şifresiyle giriş yapabilir. Yalnızca kendisine atanmış müşteri işlerini görür.
                    </p>
                  </div>
                </label>
                {formData.enableLogin && (
                  <div className="space-y-2 pl-6">
                    <Label className="flex items-center gap-1">
                      <Key className="h-3 w-3" />
                      Şifre {editing ? '(değiştirmek için doldurun)' : '*'}
                    </Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="En az 6 karakter"
                      data-testid="team-password-input"
                      autoComplete="new-password"
                    />
                    {editing && (
                      <p className="text-[10px] text-muted-foreground">
                        Boş bırakılırsa mevcut şifre korunur.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={closeDialog} className="flex-1">
                  İptal
                </Button>
                <Button type="submit" disabled={isSubmitting} className="flex-1" data-testid="team-submit-btn">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Güncelle' : 'Kaydet')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Team Grid */}
      {teamLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : team.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {team.map((member, index) => {
            const RoleIcon = roleIcons[member.role] || User;
            return (
              <Card key={member.id} className={`animate-fade-in stagger-${(index % 5) + 1}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <RoleIcon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(member)}
                        className="h-8 w-8"
                        title="Düzenle"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(member.id, member.name)}
                        className="text-destructive hover:text-destructive h-8 w-8"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <h3 className="font-semibold text-lg mb-1">{member.name}</h3>
                  <p className="text-sm text-primary font-medium mb-3">
                    {teamRoles[member.role]}
                  </p>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    {member.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        <span>{member.phone}</span>
                      </div>
                    )}
                    {member.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        <span className="truncate">{member.email}</span>
                      </div>
                    )}
                    {member.can_login ? (
                      <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-medium">
                        <ShieldCheck className="h-4 w-4" />
                        <span className="text-xs">Giriş yapabilir</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-slate-400">
                        <Lock className="h-4 w-4" />
                        <span className="text-xs">Giriş kapalı</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <UserCog className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
          <h3 className="text-lg font-medium mb-2">Henüz ekip üyesi yok</h3>
          <p className="text-muted-foreground mb-4">Yeni üye ekleyerek başlayın</p>
        </div>
      )}

      {/* Ekip Çalışması Yakında */}
      <Card className="bg-muted/30 border-dashed">
        <CardContent className="p-6 text-center">
          <UserCog className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
          <p className="text-sm font-medium text-muted-foreground">Ekip çalışması yakında</p>
          <p className="text-xs text-muted-foreground/70 mt-1">Ekip üyelerinizle ortak takvim ve görev yönetimi yakında aktif olacak</p>
        </CardContent>
      </Card>
    </div>
  );
}
